import { wtUserApi } from 'api/clients';

import type { GenericResponse } from 'commons/modules/generic-response';

import {
  ApiResponse,
  adaptApiToGenericResponse,
  adaptErrorToGenericResponse,
} from '../shared';

import type { UserConfigDto } from './dto';
import {
  mapUserTableConfigDtoToModel,
  mapUserTableConfigRequestModelToDto,
  mapUserTableConfigsListDtoToModel,
} from './mappers';
import type { UserConfig, UserConfigRequest } from './models';

const USER_TABLE_CONFIGS_PATH = '/management/user/table/configs';

const mapUserTableConfigResponseToApiResponse = (
  response: ApiResponse<UserConfigDto>,
): ApiResponse<UserConfig> => ({
  ...response,
  data: mapUserTableConfigDtoToModel(response.data),
});

const mapUserTableConfigsListResponseToApiResponse = (
  response: ApiResponse<UserConfigDto[]>,
): ApiResponse<UserConfig[]> => ({
  ...response,
  data: mapUserTableConfigsListDtoToModel(response.data),
});

export const getUserTableConfigs = async (): Promise<
  GenericResponse<ApiResponse<UserConfig[]>>
> => {
  try {
    const response = await wtUserApi.get<ApiResponse<UserConfigDto[]>>(
      USER_TABLE_CONFIGS_PATH,
    );

    const mappedResponse = mapUserTableConfigsListResponseToApiResponse(
      response.data,
    );

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al obtener información',
    );
  }
};

export const getUserTableConfigsByReportKey = async (
  tableName: string,
): Promise<GenericResponse<ApiResponse<UserConfig>>> => {
  try {
    const response = await wtUserApi.get<ApiResponse<UserConfigDto>>(
      `${USER_TABLE_CONFIGS_PATH}/${tableName}`,
    );

    const mappedResponse = mapUserTableConfigResponseToApiResponse(
      response.data,
    );

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al obtener información',
    );
  }
};

export const saveUserTableConfigs = async (
  body: UserConfigRequest,
): Promise<GenericResponse<ApiResponse<UserConfig>>> => {
  try {
    const response = await wtUserApi.post<ApiResponse<UserConfigDto>>(
      USER_TABLE_CONFIGS_PATH,
      mapUserTableConfigRequestModelToDto(body),
    );

    const mappedResponse = mapUserTableConfigResponseToApiResponse(
      response.data,
    );

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al guardar la información',
    );
  }
};

export const updateUserTableConfigs = async (
  body: UserConfigRequest,
): Promise<GenericResponse<ApiResponse<UserConfig>>> => {
  try {
    const response = await wtUserApi.put<ApiResponse<UserConfigDto>>(
      `${USER_TABLE_CONFIGS_PATH}/${body.reportKey}`,
      mapUserTableConfigRequestModelToDto(body),
    );

    const mappedResponse = mapUserTableConfigResponseToApiResponse(
      response.data,
    );

    return adaptApiToGenericResponse(mappedResponse, response.status);
  } catch (error: any) {
    return adaptErrorToGenericResponse(
      error,
      'Ocurrió un error al actualizar la información',
    );
  }
};

export const deleteUserTableConfigs = async (
  reportKey: string,
): Promise<GenericResponse<void>> => {
  try {
    const response = await wtUserApi.delete(
      `${USER_TABLE_CONFIGS_PATH}/${reportKey}`,
    );

    const result = adaptApiToGenericResponse(response.data, response.status);

    return {
      ...result,
      data: null,
    };
  } catch (error: any) {
    return adaptErrorToGenericResponse(error, 'Ocurrió un error al eliminar');
  }
};
